import { useState } from "react";
import { Routes, Route, NavLink, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/portal/AuthContext";
import { fileAuthUrl } from "@/portal/api";
import {
  LayoutDashboard, FileText, Users, UserCog, PlusCircle, LogOut, Menu, X, Eye, ShieldCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";

import Dashboard from "@/pages/portal/Dashboard";
import Applications from "@/pages/portal/Applications";
import ApplicationDetail from "@/pages/portal/ApplicationDetail";
import NewApplication from "@/pages/portal/NewApplication";
import Team from "@/pages/portal/Team";
import Profile from "@/pages/portal/Profile";

function navForRole(role) {
  if (role === "admin")
    return [
      { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
      { to: "/portal/applications", label: "Applications", icon: FileText },
      { to: "/portal/new", label: "New Application", icon: PlusCircle },
      { to: "/portal/team", label: "Users & Reps", icon: Users },
      { to: "/portal/profile", label: "Profile", icon: UserCog },
    ];
  if (role === "manager")
    return [
      { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
      { to: "/portal/applications", label: "All Applications", icon: FileText },
      { to: "/portal/new", label: "New Customer", icon: PlusCircle },
      { to: "/portal/team", label: "All Reps", icon: Users },
      { to: "/portal/profile", label: "Profile", icon: UserCog },
    ];
  if (role === "rep")
    return [
      { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
      { to: "/portal/applications", label: "Pipeline", icon: FileText },
      { to: "/portal/new", label: "New Customer", icon: PlusCircle },
      { to: "/portal/team", label: "My Team", icon: Users },
      { to: "/portal/profile", label: "Profile", icon: UserCog },
    ];
  return [
    { to: "/portal", label: "My Applications", icon: LayoutDashboard, end: true },
    { to: "/portal/new", label: "New Application", icon: PlusCircle },
    { to: "/portal/profile", label: "Profile", icon: UserCog },
  ];
}

const roleLabel = { admin: "Administrator", manager: "Manager", rep: "Representative", customer: "Customer" };

export default function PortalLayout() {
  const { user, logout, stopImpersonate } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const items = navForRole(user.role);
  const impersonating = !!user.impersonator;

  const doLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const initials = (user.name || user.email || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const SidebarInner = (
    <div className="flex h-full flex-col">
      <div className="flex h-[72px] items-center border-b border-white/10 px-6">
        <NavLink to="/portal" onClick={() => setMobileOpen(false)}>
          <img src="/ieg-lockup-dark.png?v=4" alt="IEG" className="h-9 w-auto" />
        </NavLink>
      </div>
      <nav className="flex-1 space-y-1 px-3 py-5" data-testid="portal-sidebar">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                isActive ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/5 hover:text-white"
              )
            }
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-4">
        <div className="flex items-center gap-3">
          {user.profile_picture_url ? (
            <img src={fileAuthUrl(user.profile_picture_url)} alt="" className="h-9 w-9 rounded-full object-cover" />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-energy/20 text-xs font-semibold text-energy">
              {initials}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">{user.name}</p>
            <p className="truncate text-xs text-white/50">{roleLabel[user.role]}</p>
          </div>
        </div>
        <button
          onClick={doLogout}
          data-testid="portal-logout"
          className="mt-4 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-ice">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 bg-navy lg:block">{SidebarInner}</aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-navy/60" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 bg-navy">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-4 text-white/70"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
            {SidebarInner}
          </div>
        </div>
      )}

      <div className="lg:pl-64">
        {impersonating && (
          <div className="flex flex-wrap items-center justify-center gap-3 bg-energy px-4 py-2 text-center text-sm font-medium text-navy" data-testid="impersonation-banner">
            <Eye className="h-4 w-4" />
            Acting as {user.name} ({roleLabel[user.role]})
            <button
              onClick={stopImpersonate}
              data-testid="stop-impersonation"
              className="rounded-full bg-navy px-3 py-1 text-xs font-semibold text-white hover:bg-navy/90"
            >
              Return to admin
            </button>
          </div>
        )}

        <header className="sticky top-0 z-30 flex h-[64px] items-center justify-between border-b border-line bg-white/90 px-4 backdrop-blur md:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Open menu" data-testid="mobile-portal-menu">
              <Menu className="h-6 w-6 text-navy" />
            </button>
            <h1 className="font-heading text-lg font-semibold text-navy">
              {items.find((i) => (i.end ? location.pathname === i.to : location.pathname.startsWith(i.to)))?.label || "Portal"}
            </h1>
          </div>
          <div className="flex items-center gap-2 text-xs font-medium text-slatey">
            <ShieldCheck className="h-4 w-4 text-energy" />
            {roleLabel[user.role]}
          </div>
        </header>

        <main className="p-4 md:p-8">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="applications" element={user.role === "customer" ? <Navigate to="/portal" replace /> : <Applications />} />
            <Route path="applications/:id" element={<ApplicationDetail />} />
            <Route path="new" element={<NewApplication />} />
            <Route path="team" element={user.role === "customer" ? <Navigate to="/portal" replace /> : <Team />} />
            <Route path="profile" element={<Profile />} />
            <Route path="*" element={<Navigate to="/portal" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
